import React, { useEffect } from "react";
import "./App.css";
import {
  Box,
  Card,
  Loader,
  Page,
  WixDesignSystemProvider,
  Text,
  EmptyState,
  Button,
  Layout,
  Cell,
  HorizontalTimeline,
  Thumbnail,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import { ReactTagManager } from "react-gtm-ts";
import * as Icons from "@wix/wix-ui-icons-common";
import { useTranslation } from "react-i18next";
const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

function App() {
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState<number | null>(null);
  ReactTagManager.init(tagManagerArgs);
  const token = new URLSearchParams(window.location.search).get("token");
  const instance = new URLSearchParams(window.location.search).get("instance");
  const [isUpgraded, setIsUpgraded] = React.useState(false);

  const [instanceData, setInstanceData] = React.useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  } as any);

  useEffect(() => {
    setIntercomIdentity();
    setInstance();

    // Set up interval to refresh instance data every 5 seconds
    const intervalId = setInterval(() => {
      setInstance();
    }, 5000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const BASE_URL = `https://wix.certifiedcode.io/socialboost/_functions`;

  function setIntercomIdentity() {
    fetch(BASE_URL + "/@certifiedcode/base-backend/intercom", {
      method: "GET",
      headers: {
        Authorization: instance || "",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.hash && data.email) {
          window.Intercom("boot", {
            email: data.email,
            user_hash: data.hash,
          });
        }
      })
      .catch(() => {});
  }

  function setInstance() {
    fetch(BASE_URL + "/@certifiedcode/base-backend/instance", {
      method: "GET",
      headers: {
        Authorization: instance || "",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        setInstanceData(data);
        if (data.instance?.isFree) {
          setIsUpgraded(false);
        } else {
          setIsUpgraded(true);
        }
      })
      .catch(() => {});
  }

  if (token) {
    window.location.href = `https://www.wix.com/installer/install?token=${token}&appId=800467c6-09e4-4fe5-b56f-df9c3490e3b5&redirectUrl=https://wix.certifiedcode.io/socialboost/_functions/@certifiedcode/base-backend/auth`;
    return (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text={t("loading")} />
            </Box>
          </Page.Content>
        </Page>
      </WixDesignSystemProvider>
    );
  }

  const upgradeUrl = `https://memberships.certifiedcode.us/${window.location.search}${"800467c6-09e4-4fe5-b56f-df9c3490e3b5"}?appInstanceId=${
    instanceData?.instance?.instanceId
  }`;

  const renderThumbnail = ({
    title,
    subtitle,
    id,
    productId,
    isAddOn = false,
  }: {
    title: string;
    subtitle: string;
    id: number;
    productId: string;
    isAddOn?: boolean;
  }) => {
    const getIcon = (id: number) => {
      switch (id) {
        case 0:
          return <Icons.GetStarted />; // Starting out - represents growth beginning
        case 1:
          return <Icons.Spicy />; // Boost - represents acceleration
        case 2:
          return <Icons.Hot />; // More boost - represents higher achievement
        case 3:
          return <Icons.ExtraHot />; // Maximum boost - represents premium/top tier
        default:
          return <Icons.Public />;
      }
    };

    return (
      <Cell span={6}>
        <Thumbnail
          disabled={instanceData?.instance?.billing?.packageName === productId}
          selected={selected === id}
          onClick={() => {
            setSelected(id);
            window.open(
              `https://go.certifiedcode.global/up/800467c6-09e4-4fe5-b56f-df9c3490e3b5/${
                instanceData?.instance?.instanceId
              }?productId=${productId}&billingCycle=${
                isAddOn ? "one_time" : "monthly"
              }`,
              "_blank"
            );
          }}
        >
          <Box padding="18px">
            <Box gap="12px" verticalAlign="middle">
              {getIcon(id)}
              <Box direction="vertical">
                <Text size="medium" weight="bold">
                  {title}
                </Text>
                <Box>
                  <Text size="small" secondary>
                    {subtitle}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Thumbnail>
      </Cell>
    );
  };

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      {/*<Notification theme="premium" show={isUpgradeBannerOpen} type={"sticky"}>
        <Notification.TextLabel>
          {`Upgrade to unlock "${CONTENT[index]}"`}
        </Notification.TextLabel>
        <Notification.ActionButton
          onClick={() => {
            window.open(upgradeUrl, "_blank");
          }}
        >
          Upgrade
        </Notification.ActionButton>
        <Notification.CloseButton
          onClick={() => setIsUpgradeBannerOpen(false)}
        />
      </Notification>*/}
      <Page
        height="100vh"
        // className={`${isUpgradeBannerOpen ? "App-header-notification" : ""}`}
      >
        <Page.Header
          title={"SocialBoost: Followers Growth"}
          actionsBar={
            <Box gap="SP2">
              <Button
                suffixIcon={<Icons.PremiumFilled />}
                skin="premium"
                priority="secondary"
                as="a"
                href={upgradeUrl}
                target="_blank"
              >
                {isUpgraded ? t("manage-plan") : t("pricing-button")}
              </Button>
              <Button
                skin="dark"
                onClick={() => {
                  window.Intercom("showNewMessage");
                }}
              >
                {t("contact-button")}
              </Button>
            </Box>
          }
        />
        <Page.Content>
          {!isUpgraded ? (
            <Box gap="SP3" direction="vertical">
              <EmptyState
                theme="page"
                title={"Select a goal to start"}
                subtitle={"How many followers do you want to grow per month?"}
              >
                <Layout cols={12} gap="12px">
                  {renderThumbnail({
                    title: "100 - 150 /mo",
                    subtitle: "Just starting out",
                    id: 0,
                    productId: "lite",
                  })}
                  {renderThumbnail({
                    title: "1,000 - 1,500 /mo",
                    subtitle: "Need a boost",
                    id: 1,
                    productId: "standard",
                  })}
                  {renderThumbnail({
                    title: "2,500 - 3,500+ /mo",
                    subtitle: "Need more boost",
                    id: 2,
                    productId: "plus",
                  })}
                  {renderThumbnail({
                    title: "3,500 - 5,000+ /mo",
                    subtitle: "Need a lot of boost",
                    id: 3,
                    productId: "pro",
                  })}

                  <Cell span={12}>
                    <Text secondary skin="standard" size="tiny">
                      {"Supports the following social media platforms"}
                    </Text>
                  </Cell>
                  <Cell span={12}>
                    <Box direction="horizontal" gap="SP2" align="center">
                      <Icons.Facebook />
                      <Icons.Instagram />
                      <Icons.TikTok />
                      <Icons.XCorp />
                      <Icons.YoutubeFilled />
                    </Box>
                  </Cell>
                </Layout>
              </EmptyState>
            </Box>
          ) : (
            <Layout>
              <Cell span={12}>
                {!isUpgraded && (
                  <Card>
                    <Card.Content>
                      <HorizontalTimeline
                        items={[
                          {
                            label: t("label-completed"),
                            line: "filled",
                            icon: <HorizontalTimeline.CompleteIcon />,
                          },
                          {
                            label: t("label-pending"),
                            line: "filled",
                            icon: <HorizontalTimeline.DestructiveIcon />,
                          },
                          {
                            label: t("label-working"),
                          },
                          {
                            label: t("label-live"),
                          },
                        ]}
                      />
                    </Card.Content>
                  </Card>
                )}
              </Cell>
              <Cell span={12}>
                <Card>
                  <Card.Header title={"Boost Status"} />
                  <Card.Divider />
                  <Card.Content>
                    {!isUpgraded ? (
                      <EmptyState
                        title={t("status-title")}
                        subtitle={t("status-subtitle")}
                      >
                        <Button
                          prefixIcon={<Icons.PremiumFilled />}
                          skin="premium"
                          size="large"
                          as="a"
                          href={`https://memberships.certifiedcode.us/${window.location.search}${"800467c6-09e4-4fe5-b56f-df9c3490e3b5"}?appInstanceId=${
                            instanceData?.instance?.instanceId
                          }`}
                          target="_blank"
                        >
                          {t("plan-button")}
                        </Button>
                      </EmptyState>
                    ) : (
                      <EmptyState
                        title={"Track your boost"}
                        subtitle={`We've sent you an email with the status of your boost. You can also check the status from the button below, then click "Send a Message" to get help.`}
                      >
                        <Layout cols={12} gap="12px">
                          <Cell span={12}>
                            <Button
                              prefixIcon={<Icons.ChatFilled />}
                              skin="dark"
                              size="large"
                              onClick={() => {
                                window.Intercom("showSpace", "tickets");
                              }}
                            >
                              {"View Status"}
                            </Button>
                          </Cell>
                          <Cell span={12}>
                            <Text secondary skin="standard" size="tiny">
                              {"Need more followers? Switch to a higher plan"}
                            </Text>
                          </Cell>

                          {renderThumbnail({
                            title: "100 - 150 /mo",
                            subtitle: "Just starting out",
                            id: 0,
                            productId: "lite",
                          })}
                          {renderThumbnail({
                            title: "1,000 - 1,500 /mo",
                            subtitle: "Need a boost",
                            id: 1,
                            productId: "standard",
                          })}
                          {renderThumbnail({
                            title: "2,500 - 3,500+ /mo",
                            subtitle: "Need more boost",
                            id: 2,
                            productId: "plus",
                          })}
                          {renderThumbnail({
                            title: "3,500 - 5,000+ /mo",
                            subtitle: "Need a lot of boost",
                            id: 3,
                            productId: "pro",
                          })}

                          {/* {renderThumbnail({
                            title: "150 followers pack",
                            subtitle: "Give a quick boost",
                            id: 0,
                            productId: "add-on-tier-1",
                            isAddOn: true,
                          })}
                          {renderThumbnail({
                            title: "1,500 followers pack",
                            subtitle: "Give a quick boost",
                            id: 1,
                            productId: "add-on-tier-2",
                            isAddOn: true,
                          })}
                          {renderThumbnail({
                            title: "2,500 followers pack",
                            subtitle: "Give a quick boost",
                            id: 2,
                            productId: "add-on-tier-3",
                            isAddOn: true,
                          })}
                          {renderThumbnail({
                            title: "3,500 followers pack",
                            subtitle: "Give a quick boost",
                            id: 3,
                            productId: "add-on-tier-4",
                            isAddOn: true,
                          })} */}

                          <Cell span={12}>
                            <Text secondary skin="standard" size="tiny">
                              {"Supports the following social media platforms"}
                            </Text>
                          </Cell>
                          <Cell span={12}>
                            <Box
                              direction="horizontal"
                              gap="SP2"
                              align="center"
                            >
                              <Icons.Facebook />
                              <Icons.Instagram />
                              <Icons.TikTok />
                              <Icons.XCorp />
                              <Icons.YoutubeFilled />
                            </Box>
                          </Cell>
                        </Layout>
                      </EmptyState>
                    )}
                  </Card.Content>
                </Card>
              </Cell>
            </Layout>
          )}
        </Page.Content>
      </Page>
    </WixDesignSystemProvider>
  );
}

export default App;
